import { useState } from "react";
import {
  AlertCircle,
  ChevronLeft,
  Loader2,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@sportscanner/ui";
import { courtsSchema } from "@sportscanner/schemas";
import { apiPost, getApiErrorCode } from "@/lib/api";
import type { MeSport } from "@/lib/me";
import { formatRSD } from "@/components/booking/helpers";

interface DraftCourt {
  sportId: string;
  name: string;
  isIndoor: boolean;
  pricePerSession: number;
  // null = use club default; set = override
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
}

interface Props {
  sports: MeSport[];
  clubWorkingHoursStart: string;
  clubWorkingHoursEnd: string;
  // SUPERADMIN-set cap. Null = unlimited.
  maxCourts: number | null;
  onFinish: () => Promise<void>;
  onBack: () => void;
}

export default function CourtsStep({
  sports,
  clubWorkingHoursStart,
  clubWorkingHoursEnd,
  maxCourts,
  onFinish,
  onBack,
}: Props) {
  const { t } = useTranslation();

  const [courts, setCourts] = useState<DraftCourt[]>([]);
  const [showForm, setShowForm] = useState(true);
  // Remember the last price typed so the next court draft can prefill it —
  // most clubs have similar prices across courts and this saves typing.
  const [lastPriceInput, setLastPriceInput] = useState("");

  // draft (currently being filled in form)
  const [sportId, setSportId] = useState(sports[0]?.id ?? "");
  const [name, setName] = useState("");
  const [isIndoor, setIsIndoor] = useState(false);
  const [priceInput, setPriceInput] = useState("");
  const [overrideHours, setOverrideHours] = useState(false);
  const [whStart, setWhStart] = useState(clubWorkingHoursStart);
  const [whEnd, setWhEnd] = useState(clubWorkingHoursEnd);

  const sportName = (id: string) => sports.find((s) => s.id === id)?.name ?? "";

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const atCap = maxCourts !== null && courts.length >= maxCourts;

  const trimmedName = name.trim().toLowerCase();
  const isDuplicateName =
    trimmedName.length > 0 &&
    courts.some(
      (c) =>
        c.sportId === sportId && c.name.trim().toLowerCase() === trimmedName,
    );

  const priceNumber = priceInput === "" ? NaN : Number(priceInput);
  const priceValid =
    Number.isFinite(priceNumber) &&
    priceNumber >= 0 &&
    priceNumber <= 1_000_000;

  const draftValid =
    sportId &&
    name.trim().length >= 1 &&
    priceValid &&
    (!overrideHours || whStart < whEnd) &&
    !isDuplicateName;

  // `price` defaults to the last successfully added price (used by the cancel
  // path). Pass it explicitly from handleAddDraft to avoid reading a stale
  // lastPriceInput closure on the same render that queues setLastPriceInput.
  function resetDraft(price: string = lastPriceInput) {
    setSportId(sports[0]?.id ?? "");
    setName("");
    setIsIndoor(false);
    setPriceInput(price);
    setOverrideHours(false);
    setWhStart(clubWorkingHoursStart);
    setWhEnd(clubWorkingHoursEnd);
  }

  function handleAddDraft() {
    if (!draftValid) return;
    setCourts((c) => [
      ...c,
      {
        sportId,
        name: name.trim(),
        isIndoor,
        pricePerSession: priceNumber,
        workingHoursStart: overrideHours ? whStart : null,
        workingHoursEnd: overrideHours ? whEnd : null,
      },
    ]);
    setLastPriceInput(priceInput);
    resetDraft(priceInput);
    setShowForm(false);
  }

  function handleRemove(index: number) {
    setCourts((c) => c.filter((_, i) => i !== index));
  }

  async function handleFinish() {
    if (courts.length === 0 || isSubmitting) return;

    const parsed = courtsSchema.safeParse({
      courts: courts.map((c) => ({
        sportId: c.sportId,
        name: c.name,
        isIndoor: c.isIndoor,
        pricePerSession: c.pricePerSession,
        workingHoursStart: c.workingHoursStart,
        workingHoursEnd: c.workingHoursEnd,
      })),
    });
    if (!parsed.success) {
      setErrorCode(parsed.error.issues[0]?.message ?? "VALIDATION_FAILED");
      return;
    }

    setIsSubmitting(true);
    setErrorCode(null);

    try {
      await apiPost("/api/admin/onboarding/finish", parsed.data);
      await onFinish();
    } catch (err) {
      setErrorCode(getApiErrorCode(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 space-y-6">
      {errorCode && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("common.back")}
        </button>
        <div className="space-y-1">
          <h2 className="font-heading text-lg font-semibold text-neutral-800">
            {t("onboarding.courts.title")}
          </h2>
          <p className="text-sm text-neutral-500">
            {t("onboarding.courts.description")}
          </p>
          {maxCourts !== null && (
            <p className="text-xs text-neutral-500">
              {t("onboarding.courts.maxCourtsNote", {
                count: courts.length,
                max: maxCourts,
              })}
            </p>
          )}
        </div>
      </div>

      {/* Existing courts list */}
      {courts.length > 0 && (
        <div className="space-y-2">
          {courts.map((c, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-neutral-200 bg-neutral-50/50"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800">
                  {c.name}{" "}
                  <span className="text-neutral-400 font-normal">
                    · {t(`sports.${sportName(c.sportId)}`)}
                  </span>
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {c.isIndoor
                    ? t("onboarding.courts.indoor")
                    : t("onboarding.courts.outdoor")}
                  {" · "}
                  {formatRSD(
                    c.pricePerSession,
                    t("bookings.details.free"),
                  )} ·{" "}
                  {c.workingHoursStart && c.workingHoursEnd
                    ? `${c.workingHoursStart}–${c.workingHoursEnd}`
                    : `${clubWorkingHoursStart}–${clubWorkingHoursEnd}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                disabled={isSubmitting}
                className="text-neutral-400 hover:text-red-500 transition-colors p-1 cursor-pointer disabled:cursor-not-allowed"
                aria-label={t("common.delete")}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {courts.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-neutral-200 px-4 py-8 text-center">
          <p className="text-sm text-neutral-500">
            {t("onboarding.courts.empty")}
          </p>
        </div>
      )}

      {/* Inline draft form */}
      {showForm ? (
        <div className="rounded-lg border border-neutral-200 p-4 space-y-4 bg-white">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-800">
              {t("onboarding.courts.addCourt")}
            </h3>
            {courts.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  resetDraft();
                  setShowForm(false);
                }}
                className="text-neutral-400 hover:text-neutral-600 transition-colors cursor-pointer"
                aria-label={t("common.cancel")}
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Sport */}
          <div className="space-y-2">
            <Label className="text-sm text-neutral-700">
              {t("onboarding.courts.sportLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Select
              value={sportId}
              onValueChange={setSportId}
              disabled={isSubmitting}
            >
              <SelectTrigger className="rounded-lg border-neutral-200 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sports.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {t(`sports.${s.name}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="court-name" className="text-sm text-neutral-700">
              {t("onboarding.courts.nameLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="court-name"
              type="text"
              placeholder={t("onboarding.courts.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSubmitting}
              className={`rounded-lg border-neutral-200 ${isDuplicateName ? "border-red-400" : ""}`}
            />
            {isDuplicateName ? (
              <p className="text-xs text-red-500">
                {t("onboarding.courts.duplicateName")}
              </p>
            ) : (
              <p className="text-xs text-neutral-500">
                {t("onboarding.courts.nameHelp")}
              </p>
            )}
          </div>

          {/* Indoor toggle */}
          <div className="flex items-center justify-between">
            <Label className="text-sm text-neutral-700">
              {t("onboarding.courts.indoorLabel")}
            </Label>
            <div className="flex items-center gap-2">
              <span
                className={`text-xs ${
                  !isIndoor
                    ? "text-neutral-800 font-medium"
                    : "text-neutral-400"
                }`}
              >
                {t("onboarding.courts.outdoor")}
              </span>
              <Switch
                checked={isIndoor}
                onCheckedChange={setIsIndoor}
                disabled={isSubmitting}
              />
              <span
                className={`text-xs ${
                  isIndoor ? "text-neutral-800 font-medium" : "text-neutral-400"
                }`}
              >
                {t("onboarding.courts.indoor")}
              </span>
            </div>
          </div>

          {/* Price */}
          <div className="space-y-2">
            <Label htmlFor="court-price" className="text-sm text-neutral-700">
              {t("onboarding.courts.priceLabel")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="court-price"
                type="number"
                inputMode="numeric"
                min={0}
                step={50}
                placeholder="0"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                disabled={isSubmitting}
                className="rounded-lg border-neutral-200 w-40"
              />
              <span className="text-sm text-neutral-500">RSD</span>
            </div>
          </div>

          {/* Working hours: same as club by default, opt-in override */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-neutral-700">
                {t("onboarding.courts.workingHoursLabel")}
              </Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">
                  {t("courts.row.overrideHours")}
                </span>
                <Switch
                  checked={overrideHours}
                  onCheckedChange={(v) => {
                    setOverrideHours(v);
                    if (v) {
                      // Prefill from current club default so the user can tweak.
                      setWhStart(clubWorkingHoursStart);
                      setWhEnd(clubWorkingHoursEnd);
                    }
                  }}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            {overrideHours && (
              <div className="flex items-center gap-2 sm:gap-3">
                <Input
                  type="time"
                  value={whStart}
                  onChange={(e) => setWhStart(e.target.value)}
                  disabled={isSubmitting}
                  className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                />
                <span className="text-neutral-400">—</span>
                <Input
                  type="time"
                  value={whEnd}
                  onChange={(e) => setWhEnd(e.target.value)}
                  disabled={isSubmitting}
                  className="rounded-lg border-neutral-200 flex-1 sm:flex-initial sm:w-32"
                />
              </div>
            )}
            <p className="text-xs text-neutral-500">
              {t("onboarding.courts.workingHoursHelp")}
            </p>
          </div>

          <Button
            type="button"
            onClick={handleAddDraft}
            disabled={!draftValid || isSubmitting || atCap}
            className="w-full py-2.5 h-auto bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            {t("onboarding.courts.addToList")}
          </Button>
          {atCap && (
            <p className="text-xs text-amber-600">
              {t("onboarding.courts.maxReached", { max: maxCourts })}
            </p>
          )}
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={() => setShowForm(true)}
          disabled={isSubmitting || atCap}
          className="w-full py-2.5 h-auto rounded-full cursor-pointer"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          {t("onboarding.courts.addCourt")}
        </Button>
      )}

      {/* Finish */}
      <div className="pt-2 border-t border-neutral-100">
        <Button
          type="button"
          onClick={handleFinish}
          disabled={courts.length === 0 || isSubmitting}
          className="w-full py-3 h-auto bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-full cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("onboarding.finishing")}
            </>
          ) : (
            t("onboarding.finish")
          )}
        </Button>
      </div>
    </div>
  );
}
